// Team Profile Interview Types for CARR Biosystems Hub
// Questions adapted from Made Scientific intel_questions database

export interface InterviewQuestion {
  id: number;
  categoryId: string;
  text: string;
  subtext: string;
  emoji: string;
}

export interface InterviewCategory {
  id: string;
  label: string;
  emoji: string;
  questions: InterviewQuestion[];
}

export interface InterviewResponse {
  question_id: number;
  question_text: string;
  response_text: string;
  response_type: 'text' | 'audio' | 'both';
  audio_url?: string;
  transcript?: string;
  answered_at: string;
}

export interface InterviewData {
  responses?: InterviewResponse[];
}

export interface TeamProfileForInterview {
  id: string;
  full_name: string;
  job_title: string | null;
  department: string | null;
  territory: string | null;
  company: string | null;
  interview_data: InterviewData | null;
  interview_questions_answered: number;
  interview_completed_at: string | null;
}

// ---------------------------------------------------------------------------
// 18 Interview Questions — adapted from Made Scientific intel_questions
// Grouped into 6 categories, 3 questions each
// ---------------------------------------------------------------------------

export const INTERVIEW_CATEGORIES: InterviewCategory[] = [
  {
    id: 'industry_trends',
    label: 'Industry Trends',
    emoji: '🔮',
    questions: [
      {
        id: 1,
        categoryId: 'industry_trends',
        text: "What technology shifts will impact cell therapy manufacturing in the next 2 years?",
        subtext: "Automation, AI, closed systems, etc.",
        emoji: '🔮',
      },
      {
        id: 2,
        categoryId: 'industry_trends',
        text: "How is the funding environment affecting client decisions?",
        subtext: "Are companies being more cautious? Seeking flexibility?",
        emoji: '💰',
      },
      {
        id: 3,
        categoryId: 'industry_trends',
        text: "What regulatory changes should we be watching?",
        subtext: "FDA guidance, international requirements",
        emoji: '📋',
      },
    ],
  },
  {
    id: 'personal_expertise',
    label: 'Personal Expertise',
    emoji: '🎓',
    questions: [
      {
        id: 4,
        categoryId: 'personal_expertise',
        text: "What's a lesson from your industry experience that's relevant today?",
        subtext: "Supply chain, scale-up, client relationships",
        emoji: '🎓',
      },
      {
        id: 5,
        categoryId: 'personal_expertise',
        text: "What's your contrarian view that most people in the industry disagree with?",
        subtext: "Bold opinions make great content",
        emoji: '🔥',
      },
      {
        id: 6,
        categoryId: 'personal_expertise',
        text: "What story from your career best illustrates CARR Biosystems' value?",
        subtext: "A specific project or client win",
        emoji: '📖',
      },
    ],
  },
  {
    id: 'client_intelligence',
    label: 'Client Intelligence',
    emoji: '😓',
    questions: [
      {
        id: 7,
        categoryId: 'client_intelligence',
        text: "What common pain points do prospects mention in discovery calls?",
        subtext: "Manufacturing, timeline, cost, quality concerns",
        emoji: '😓',
      },
      {
        id: 8,
        categoryId: 'client_intelligence',
        text: "What made recent wins choose CARR Biosystems over competitors?",
        subtext: "Key differentiators that closed deals",
        emoji: '🏆',
      },
      {
        id: 9,
        categoryId: 'client_intelligence',
        text: "What objections do you hear most often?",
        subtext: "Price, timeline, capabilities, location",
        emoji: '🛑',
      },
    ],
  },
  {
    id: 'content_ideas',
    label: 'Content Ideas',
    emoji: '❓',
    questions: [
      {
        id: 10,
        categoryId: 'content_ideas',
        text: "What questions do prospects ask that would make good LinkedIn posts?",
        subtext: "Educational content opportunities",
        emoji: '❓',
      },
      {
        id: 11,
        categoryId: 'content_ideas',
        text: "What recent news sparked interesting client conversations?",
        subtext: "Topics with natural engagement",
        emoji: '📰',
      },
      {
        id: 12,
        categoryId: 'content_ideas',
        text: "What would you want to tell every cell therapy CEO?",
        subtext: "Your soapbox moment",
        emoji: '📢',
      },
    ],
  },
  {
    id: 'market_challenges',
    label: 'Market Challenges',
    emoji: '🔥',
    questions: [
      {
        id: 13,
        categoryId: 'market_challenges',
        text: "What's the #1 challenge you're hearing from cell therapy companies right now?",
        subtext: "Think about recent calls and meetings",
        emoji: '🔥',
      },
      {
        id: 14,
        categoryId: 'market_challenges',
        text: "Which therapeutic areas are showing the most momentum?",
        subtext: "CAR-T, TCR, NK cells, iPSC, etc.",
        emoji: '📈',
      },
      {
        id: 15,
        categoryId: 'market_challenges',
        text: "What's the biggest misconception clients have about bioprocessing equipment partnerships?",
        subtext: "What do you find yourself correcting often?",
        emoji: '🤔',
      },
    ],
  },
  {
    id: 'competitive_intel',
    label: 'Competitive Intel',
    emoji: '🎯',
    questions: [
      {
        id: 16,
        categoryId: 'competitive_intel',
        text: "Which competitors are you seeing most in deals?",
        subtext: "Who keeps coming up in conversations?",
        emoji: '🎯',
      },
      {
        id: 17,
        categoryId: 'competitive_intel',
        text: "What are competitors doing that we should pay attention to?",
        subtext: "New capabilities, pricing, partnerships",
        emoji: '👀',
      },
      {
        id: 18,
        categoryId: 'competitive_intel',
        text: "How do you position CARR Biosystems against competitors?",
        subtext: "Your go-to differentiation points",
        emoji: '💪',
      },
    ],
  },
];

// Flat list of all questions for convenience
export const ALL_INTERVIEW_QUESTIONS: InterviewQuestion[] = INTERVIEW_CATEGORIES.flatMap(
  (cat) => cat.questions
);

export const TOTAL_QUESTIONS = ALL_INTERVIEW_QUESTIONS.length; // 18
